import type { GlobalSearchCategory } from "@/lib/cosmos-api";

export type StoredRecentSearch = {
  query: string;
  category: GlobalSearchCategory;
  createdAt: string;
};

const STORAGE_PREFIX = "cosmos-recent-searches";
const MAX_RECENT_SEARCHES = 8;

const storageKey = (scope: string): string => `${STORAGE_PREFIX}:${scope}`;

const normalizeQuery = (query: string): string => query.trim();

export const readRecentSearches = (scope: string): StoredRecentSearch[] => {
  if (typeof window === "undefined") {
    return [];
  }
  try {
    const raw = localStorage.getItem(storageKey(scope));
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .filter(
        (entry): entry is StoredRecentSearch =>
          typeof entry === "object" &&
          entry !== null &&
          typeof (entry as StoredRecentSearch).query === "string" &&
          typeof (entry as StoredRecentSearch).category === "string" &&
          typeof (entry as StoredRecentSearch).createdAt === "string"
      )
      .slice(0, MAX_RECENT_SEARCHES);
  } catch {
    return [];
  }
};

export const writeRecentSearches = (
  scope: string,
  searches: StoredRecentSearch[],
): void => {
  if (typeof window === "undefined") {
    return;
  }
  localStorage.setItem(
    storageKey(scope),
    JSON.stringify(searches.slice(0, MAX_RECENT_SEARCHES)),
  );
};

export const mergeRecentSearches = (
  current: StoredRecentSearch[],
  incoming: StoredRecentSearch[],
): StoredRecentSearch[] => {
  const merged = [...incoming, ...current].sort(
    (left, right) =>
      new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
  );
  const seen = new Set<string>();
  const results: StoredRecentSearch[] = [];
  for (const entry of merged) {
    const query = normalizeQuery(entry.query);
    if (!query) {
      continue;
    }
    const key = query.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    results.push({
      query,
      category: entry.category ?? "All",
      createdAt: entry.createdAt,
    });
    if (results.length >= MAX_RECENT_SEARCHES) {
      break;
    }
  }
  return results;
};

export const rememberRecentSearch = (
  scope: string,
  query: string,
  category: GlobalSearchCategory = "All",
): StoredRecentSearch[] => {
  const trimmed = normalizeQuery(query);
  if (!trimmed) {
    return readRecentSearches(scope);
  }
  const next = mergeRecentSearches(readRecentSearches(scope), [
    {
      query: trimmed,
      category,
      createdAt: new Date().toISOString(),
    },
  ]);
  writeRecentSearches(scope, next);
  return next;
};

export const resolveRecentSearchScope = (input: {
  tenantId?: string;
  userId?: string;
}): string => `${input.tenantId ?? "default-tenant"}:${input.userId ?? "local-cosmos-user"}`;
